import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { useSeo } from "@/lib/seo";
import { X } from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "Tout" },
  { id: "mariage", label: "Mariage" },
  { id: "anniversaire", label: "Anniversaire" },
  { id: "entreprise", label: "Entreprise" },
  { id: "collectivite", label: "Mairie & Collectivité" },
  { id: "senior", label: "Résidence senior" },
  { id: "maxence", label: "Maxence Berche" },
];

// Repertoire d'images vraies, taguées par catégorie
const IMAGES = [
  // Mariage
  { src: "/assets/mariages/mariage-01.jpg", cat: "mariage", tag: "Mariage" },
  // Anniversaire (8 photos)
  ...Array.from({ length: 8 }, (_, i) => ({
    src: `/assets/anniversaires/anniversaire-${String(i + 1).padStart(2, "0")}.jpg`,
    cat: "anniversaire",
    tag: "Anniversaire",
  })),
  // Entreprises (8 photos — sous-ensemble corporate)
  ...[1, 4, 5, 6, 7, 9, 10, 11].map((n) => ({
    src: `/assets/entreprises/entreprise-${String(n).padStart(2, "0")}.jpg`,
    cat: "entreprise",
    tag: "Entreprise",
  })),
  // Collectivités (4 photos — sous-ensemble mairies/publics)
  ...[2, 3, 8, 12].map((n) => ({
    src: `/assets/entreprises/entreprise-${String(n).padStart(2, "0")}.jpg`,
    cat: "collectivite",
    tag: "Collectivité",
  })),
  // Senior
  { src: "/assets/seniors/seniors-01.jpg", cat: "senior", tag: "Résidence senior" },
  // Maxence Berche (5 photos)
  ...Array.from({ length: 5 }, (_, i) => ({
    src: `/assets/maxence/maxence-${String(i + 1).padStart(2, "0")}.jpg`,
    cat: "maxence",
    tag: "Maxence Berche",
  })),
];

export default function Galerie() {
  useSeo({
    title: "Réalisations DJ événementiel | My Animation",
    description:
      "Découvrez les ambiances, soirées, mariages, photobooth et mises en lumière réalisés par My Animation dans les Hauts-de-France.",
    path: "/galerie",
    keywords: "réalisations DJ événementiel Hauts-de-France, galerie mariage Lille, photobooth Nord, ambiances soirées",
  });

  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? IMAGES : IMAGES.filter((i) => i.cat === filter);

  return (
    <>
      <PageHero
        eyebrow="Galerie"
        title={<>Quelques <span className="italic gold-text">instants</span><br/>capturés.</>}
        subtitle="Un aperçu des événements orchestrés par My Animation — mariages, anniversaires, entreprises, mairies, résidences seniors et concerts live."
        img="/assets/entreprises/entreprise-04.jpg"
      />

      {/* Filtres */}
      <section className="px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto pt-16">
        <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`gallery-filter-${c.id}`}
              onClick={() => setFilter(c.id)}
              className={`text-[11px] uppercase tracking-[0.18em] px-4 py-2.5 border transition ${
                filter === c.id
                  ? "bg-[#DCB457] text-black border-[#DCB457]"
                  : "border-[#DCB457]/25 text-white/70 hover:border-[#DCB457] hover:text-[#DCB457]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Grille */}
      <section className="py-12 md:py-16 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
          {filtered.map((img, i) => (
            <button
              key={img.src + i}
              type="button"
              data-testid={`gallery-item-${i}`}
              onClick={() => setActive(img)}
              className="block mb-4 md:mb-6 break-inside-avoid w-full text-left group relative overflow-hidden card-luxe !p-0"
            >
              <img
                src={img.src}
                alt={img.tag}
                loading="lazy"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                <span className="font-serif-luxe italic text-base md:text-lg text-white/95">{img.tag}</span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#DCB457]">Voir</span>
              </div>
            </button>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-white/55 py-20">Aucune photo dans cette catégorie pour le moment.</p>
        )}
      </section>

      {active && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 text-white/80 hover:text-[#DCB457]"
            onClick={() => setActive(null)}
            aria-label="Fermer"
          >
            <X size={28} strokeWidth={1.2} />
          </button>
          <img
            src={active.src}
            alt={active.tag}
            className="max-h-[88vh] max-w-[92vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <CTAFooter />
    </>
  );
}
